import React from "react";
import { InsightData } from "../../types/insightDashboard";
import { NumberCard } from "./widgets/NumberCard";
import { ComparisonAreaChart } from "./widgets/ComparisonAreaChart";
import { MonthlyComparisonBarChart } from "./widgets/MonthlyComparisonBarChart";
import { BreakdownBarChart } from "./widgets/BreakdownBarChart";
import { TopList } from "./widgets/TopList";
import { HorizontalBarChart } from "./widgets/HorizontalBarChart";
import { MeasureToggleBarChart } from "./widgets/MeasureToggleBarChart";
import { PieChart } from "./widgets/PieChart";
import { StackedPercentageBarChart } from "./widgets/StackedPercentageBarChart";

export interface WidgetConfig {
  type:
    | "numberCard"
    | "comparisonAreaChart"
    | "monthlyComparisonBarChart"
    | "breakdownBarChart"
    | "topList"
    | "horizontalBarChart"
    | "measureToggleBarChart"
    | "pieChart"
    | "stackedPercentageBarChart";
  props: any; // This will be specific to each widget type
  getData?: (insightData: InsightData) => any;
  colSpan?: 1 | 2 | 3 | 4; // Grid column span (defaults vary by widget type)
}

interface DashboardProps {
  data: InsightData;
  config: WidgetConfig[];
}

const defaultColSpans: Record<WidgetConfig["type"], number> = {
  numberCard: 1,
  comparisonAreaChart: 4,
  monthlyComparisonBarChart: 4,
  breakdownBarChart: 2,
  topList: 2,
  horizontalBarChart: 2,
  measureToggleBarChart: 2,
  pieChart: 2,
  stackedPercentageBarChart: 2,
};

const colSpanClasses: Record<number, string> = {
  1: "",
  2: "tw:md:col-span-2",
  3: "tw:md:col-span-2 tw:lg:col-span-3",
  4: "tw:md:col-span-2 tw:lg:col-span-4",
};

export function Dashboard({ data, config }: DashboardProps) {
  const renderWidget = (widgetConfig: WidgetConfig) => {
    switch (widgetConfig.type) {
      case "numberCard":
        return <NumberCard {...widgetConfig.props} />;
      case "comparisonAreaChart":
        return <ComparisonAreaChart {...widgetConfig.props} />;
      case "monthlyComparisonBarChart":
        return <MonthlyComparisonBarChart {...widgetConfig.props} />;
      case "breakdownBarChart":
        return <BreakdownBarChart {...widgetConfig.props} />;
      case "topList":
        return <TopList {...widgetConfig.props} />;
      case "horizontalBarChart":
        return <HorizontalBarChart {...widgetConfig.props} />;
      case "measureToggleBarChart":
        return <MeasureToggleBarChart {...widgetConfig.props} />;
      case "pieChart":
        return <PieChart {...widgetConfig.props} />;
      case "stackedPercentageBarChart":
        return <StackedPercentageBarChart {...widgetConfig.props} />;
      default:
        return null;
    }
  };

  return (
    <div className="tw:grid tw:gap-4 tw:md:grid-cols-2 tw:lg:grid-cols-4">
      {config.map((widgetConfig, index) => {
        const colSpan = widgetConfig.colSpan ?? defaultColSpans[widgetConfig.type];
        const spanClass = colSpanClasses[colSpan];
        return (
          <div key={index} className={`${spanClass} tw:h-full tw:min-w-0`}>
            {renderWidget(widgetConfig)}
          </div>
        );
      })}
    </div>
  );
}
